import { Suspense, lazy, useState, useEffect, useCallback, useRef } from 'react';
import { useAccountSettings, SettingsSection } from '@/context/AccountSettingsContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';

// Lazy load section components
const ProfileSection = lazy(() => import('./sections/ProfileSection'));
const PlanSection = lazy(() => import('./sections/PlanSection'));
const SecuritySection = lazy(() => import('./sections/SecuritySection'));

const SectionLoader = () => (
  <div className="space-y-4">
    <Skeleton className="h-8 w-48" />
    <Skeleton className="h-4 w-64" />
    <div className="space-y-3 mt-6">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-3/4" />
    </div>
  </div>
);

// Hook to highlight text in the DOM and track sections
const useTextHighlight = (
  searchQuery: string,
  containerRef: React.RefObject<HTMLDivElement>,
  onSectionChange: (section: SettingsSection) => void
) => {
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const highlightClass = 'settings-search-highlight';
  const currentClass = 'settings-search-highlight-current';

  const clearHighlights = useCallback(() => {
    if (!containerRef.current) return;
    const highlights = containerRef.current.querySelectorAll(`.${highlightClass}`);
    highlights.forEach((el) => {
      const parent = el.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(el.textContent || ''), el);
        parent.normalize();
      }
    });
  }, [containerRef, highlightClass]);

  const highlightText = useCallback((node: Node, query: string): number => {
    let count = 0;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const lowerText = text.toLowerCase();
      const lowerQuery = query.toLowerCase();
      let index = lowerText.indexOf(lowerQuery);

      if (index !== -1) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        while (index !== -1) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex, index)));
          const mark = document.createElement('mark');
          mark.className = highlightClass;
          mark.textContent = text.substring(index, index + query.length);
          fragment.appendChild(mark);
          count++;
          lastIndex = index + query.length;
          index = lowerText.indexOf(lowerQuery, lastIndex);
        }

        fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        node.parentNode?.replaceChild(fragment, node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      // Skip input, textarea, script, and section header elements
      if (['INPUT', 'TEXTAREA', 'SCRIPT', 'STYLE', 'MARK'].includes(element.tagName)) {
        return count;
      }
      // Skip section headers
      if (element.hasAttribute('data-section-header')) {
        return count;
      }
      const childNodes = Array.from(node.childNodes);
      childNodes.forEach((child) => {
        count += highlightText(child, query);
      });
    }
    return count;
  }, [highlightClass]);

  const applyHighlights = useCallback(() => {
    if (!containerRef.current || !searchQuery.trim()) {
      setMatchCount(0);
      setCurrentMatch(0);
      return;
    }

    clearHighlights();
    const count = highlightText(containerRef.current, searchQuery);
    setMatchCount(count);
    setCurrentMatch(count > 0 ? 1 : 0);

    // Navigate to first match
    if (count > 0) {
      const firstMatch = containerRef.current.querySelector(`.${highlightClass}`);
      if (firstMatch) {
        firstMatch.classList.add(currentClass);
        // Find which section this match is in
        const sectionEl = firstMatch.closest('[data-section]');
        if (sectionEl) {
          const section = sectionEl.getAttribute('data-section') as SettingsSection;
          onSectionChange(section);
        }
        setTimeout(() => {
          firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [searchQuery, containerRef, clearHighlights, highlightText, highlightClass, currentClass, onSectionChange]);

  const goToMatch = useCallback((direction: 'next' | 'prev') => {
    if (!containerRef.current || matchCount === 0) return;

    const highlights = containerRef.current.querySelectorAll(`.${highlightClass}`);
    highlights.forEach(el => el.classList.remove(currentClass));

    let newIndex = currentMatch;
    if (direction === 'next') {
      newIndex = currentMatch >= matchCount ? 1 : currentMatch + 1;
    } else {
      newIndex = currentMatch <= 1 ? matchCount : currentMatch - 1;
    }

    setCurrentMatch(newIndex);
    const targetMatch = highlights[newIndex - 1];
    if (targetMatch) {
      targetMatch.classList.add(currentClass);
      // Find which section this match is in and switch to it
      const sectionEl = targetMatch.closest('[data-section]');
      if (sectionEl) {
        const section = sectionEl.getAttribute('data-section') as SettingsSection;
        onSectionChange(section);
      }
      setTimeout(() => {
        targetMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [containerRef, matchCount, currentMatch, highlightClass, currentClass, onSectionChange]);

  useEffect(() => {
    const timer = setTimeout(applyHighlights, 300);
    return () => {
      clearTimeout(timer);
      clearHighlights();
    };
  }, [searchQuery, applyHighlights, clearHighlights]);

  return { matchCount, currentMatch, goToMatch };
};

export const AccountSettingsContent: React.FC = () => {
  const { activeSection, setActiveSection } = useAccountSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const handleSectionChange = useCallback((section: SettingsSection) => {
    setActiveSection(section);
  }, [setActiveSection]);

  const { matchCount, currentMatch, goToMatch } = useTextHighlight(searchQuery, contentRef, handleSectionChange);

  // All sections with their content
  const sections: { id: SettingsSection; content: React.ReactNode }[] = [
    {
      id: 'profile',
      content: <ProfileSection />,
    },
    {
      id: 'plan',
      content: <PlanSection />,
    },
    {
      id: 'security',
      content: <SecuritySection />,
    },
  ];

  return (
    <div className="w-full space-y-4">
      {/* Search Bar */}
      <div className="sticky top-0 z-10 bg-background pb-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search across all settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-20"
            />
            {searchQuery && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <span className="text-xs text-muted-foreground min-w-[60px] text-right">
                  {matchCount > 0 ? `${currentMatch}/${matchCount}` : 'No results'}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => goToMatch('prev')}
                  disabled={matchCount === 0}
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => goToMatch('next')}
                  disabled={matchCount === 0}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content with highlighting - render all sections, show only active */}
      <div ref={contentRef}>
        <Suspense fallback={<SectionLoader />}>
          {sections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <div
                key={section.id}
                data-section={section.id}
                className={isActive ? 'block' : 'hidden'}
              >
                <div className="space-y-6">
                  {section.content}
                </div>
              </div>
            );
          })}
        </Suspense>
      </div>

      {/* Highlight Styles */}
      <style>{`
        .settings-search-highlight {
          background-color: rgba(255, 235, 59, 0.4);
          padding: 0 2px;
          border-radius: 2px;
        }
        .settings-search-highlight-current {
          background-color: rgba(255, 152, 0, 0.6);
          outline: 2px solid rgba(255, 152, 0, 0.8);
        }
      `}</style>
    </div>
  );
};

export default AccountSettingsContent;
